import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, request } from '../lib/api.ts';
import { dateTime, since } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';

/**
 * Incoming e-mail: the business's own mailboxes (support@, sales@, info@), read by ERP PRO. Support
 * e-mail becomes tickets, sales e-mail becomes opportunities, and the rest waits in a shared inbox
 * for a person to turn it into one or the other, or set it aside.
 */

type Route = 'helpdesk' | 'crm' | 'keep';
type Security = 'ssl' | 'starttls';
type State = 'new' | 'done' | 'ignored' | 'failed';
interface BoxRow {
  id: string;
  name: string;
  email: string;
  route: Route;
  active: boolean;
  host?: string;
  port?: number;
  security?: Security;
  username?: string;
  folder?: string;
  checked_at?: string | null;
  ok_at?: string | null;
  error: string | null;
  failures?: number;
  password_readable?: boolean;
  waiting: number;
  today: number;
}
interface Overview {
  boxes: BoxRow[];
  can: { manage: boolean; seal: boolean; helpdesk: boolean; crm: boolean };
}
interface Row {
  id: string;
  box: string;
  box_name: string;
  from_name: string | null;
  from_email: string | null;
  subject: string | null;
  sent_at: string | null;
  received_at: string;
  state: State;
  automatic: boolean;
  result_kind: 'ticket' | 'lead' | 'outreach' | null;
  result_id: string | null;
  result_label: string | null;
  note: string | null;
  preview: string | null;
  files: number;
}
interface Full extends Row {
  to_list: string[];
  cc_list: string[];
  body: string | null;
  new_part: string;
  box_email: string;
  handled_by_name: string | null;
  handled_at: string | null;
  files: never;
  can: { helpdesk: boolean; crm: boolean };
}
interface FullFiles {
  files: { id: string; name: string; mime: string; size: number; kept: boolean }[];
}

const ROUTE_ICON: Record<Route, IconName> = { helpdesk: 'lifebuoy', crm: 'trendUp', keep: 'inbox' };
const FILTERS = ['new', 'done', 'ignored', 'failed', 'all'] as const;
type Filter = (typeof FILTERS)[number];

const sizeText = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : n >= 1000 ? `${Math.round(n / 1000)} KB` : `${n} B`);
const who = (m: Pick<Row, 'from_name' | 'from_email'>) => m.from_name || m.from_email || t('mb.unknown_sender');
const resultLink = (m: Pick<Row, 'result_kind' | 'result_id'>) =>
  m.result_kind === 'ticket' ? `/helpdesk?t=${m.result_id}` : m.result_kind === 'outreach' ? `/outreach?co=${m.result_id}` : `/crm?lead=${m.result_id}`;

function boxState(b: BoxRow): { tone: Tone; icon: IconName; label: string } {
  if (!b.active) return { tone: 'neutral', icon: 'pause', label: t('mb.st_off') };
  if (b.error) return { tone: 'danger', icon: 'alert', label: t('mb.st_error') };
  if (!b.ok_at && b.checked_at === null) return { tone: 'info', icon: 'clock', label: t('mb.st_waiting') };
  return { tone: 'ok', icon: 'checkCircle', label: t('mb.st_ok') };
}

function msgState(m: Row): { tone: Tone; icon: IconName; label: string } {
  if (m.state === 'done') {
    if (m.result_kind === 'ticket') return { tone: 'ok', icon: 'lifebuoy', label: m.result_label ?? t('mb.ticket') };
    if (m.result_kind === 'outreach') return { tone: 'ok', icon: 'reply', label: t('mb.outreach') };
    return { tone: 'ok', icon: 'trendUp', label: t('mb.lead') };
  }
  if (m.state === 'ignored') return { tone: 'neutral', icon: 'archive', label: t('mb.f_ignored') };
  if (m.state === 'failed') return { tone: 'danger', icon: 'alert', label: t('mb.f_failed') };
  return { tone: 'info', icon: 'inbox', label: t('mb.f_new') };
}

/** Server notes are English; the common ones have their own words. */
const NOTES: Record<string, MessageKey> = {
  'Automatic e-mail (out of office, delivery report or newsletter).': 'mb.note_automatic',
  'Sent from one of the business’s own addresses.': 'mb.note_own',
  'Too many e-mails from this address today.': 'mb.note_flood',
  'Too large to bring in. Open it in the mailbox.': 'mb.note_large',
  'This e-mail could not be read. Open it in the mailbox.': 'mb.note_unreadable',
  'Added to the ticket.': 'mb.note_added',
  'Set aside.': 'mb.note_set_aside',
  'The sender’s address is missing.': 'mb.note_no_sender',
  'Helpdesk is not in the plan.': 'mb.note_no_helpdesk',
  'CRM is not in the plan.': 'mb.note_no_crm',
  'Bounce: the address was marked as bounced in Sales outreach.': 'mb.note_outreach_bounce',
};
const noteText = (n: string | null) => (n ? (NOTES[n] ? t(NOTES[n]) : n) : null);
/** What the mail server answered, in the reader's words when it is one of the usual answers. */
const SERVER: Record<string, MessageKey> = {
  'The mail server refused the user name or password.': 'mb.err_login',
  'The mail server could not be found.': 'mb.err_not_found',
  'The mail server could not be found, or it is on a private network.': 'mb.err_not_found',
  'The mail server refused the connection. Check the port.': 'mb.err_refused',
  'The mail server did not answer.': 'mb.err_timeout',
  'The mail server’s security certificate is not valid.': 'mb.err_cert',
  'The password could not be opened on this server. Type it again.': 'mb.err_password',
};
const serverText = (e: string) => (SERVER[e] ? t(SERVER[e]) : e);

// ---- the page -----------------------------------------------------------------------------------

type Tab = 'inbox' | 'boxes';

export function Mailbox() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const manage = can('mailbox.manage');
  const { data: o, reload } = useAction<Overview>('mailbox.overview');
  const tab: Tab = manage && params.get('tab') === 'boxes' ? 'boxes' : 'inbox';
  const setTab = (k: Tab) => setParams(k === 'inbox' ? {} : { tab: k }, { replace: true });
  const [edit, setEdit] = useState<BoxRow | 'new' | null>(null);
  const waiting = o?.boxes.reduce((s, b) => s + b.waiting, 0) ?? 0;
  const broken = o?.boxes.filter((b) => b.active && b.error).length ?? 0;
  return (
    <Shell>
      <div className="page mb-page">
        <div className="page-head">
          <div>
            <h1>{t('mb.title')}</h1>
            <p className="muted">{t('mb.lead')}</p>
          </div>
          {manage && o?.can.seal && (tab === 'boxes' || !o.boxes.length) ? (
            <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
              {t('mb.connect')}
            </Button>
          ) : null}
        </div>
        {manage ? (
          <div className="segmented tabs" role="tablist" aria-label={t('mb.sections')}>
            {(['inbox', 'boxes'] as const).map((k) => (
              <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
                <Icon name={k === 'inbox' ? 'inbox' : 'mail'} size={16} />
                {t(`mb.tab_${k}`)}
                {k === 'inbox' && waiting ? <span className="dev-tab-count">{waiting}</span> : null}
                {k === 'boxes' && broken ? <span className="dev-tab-count mb-count-bad">{broken}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
        {!o ? (
          <Skeleton h={360} r="var(--radius-lg)" />
        ) : !o.boxes.length ? (
          <section className="panel">
            <EmptyState
              icon="inbox"
              title={t('mb.none_title')}
              body={manage ? t('mb.none_body') : t('mb.none_body_staff')}
              action={
                manage && o.can.seal ? (
                  <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
                    {t('mb.connect')}
                  </Button>
                ) : undefined
              }
            />
            {manage ? <HowItWorks /> : null}
          </section>
        ) : tab === 'boxes' ? (
          <BoxesTab o={o} onEdit={setEdit} onChange={reload} />
        ) : (
          <InboxTab o={o} onChange={reload} />
        )}
        {manage && o && !o.can.seal ? (
          <p className="callout callout-warn">
            <Icon name="lock" size={18} />
            <span>{t('mb.no_seal')}</span>
          </p>
        ) : null}
        {edit && o ? <BoxDialog o={o} box={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={reload} /> : null}
      </div>
    </Shell>
  );
}

function HowItWorks() {
  return (
    <ol className="mb-how">
      {(['route', 'read', 'safe'] as const).map((k, i) => (
        <li key={k}>
          <span className="dev-step-n">{i + 1}</span>
          <span>
            <strong>{t(`mb.how_${k}`)}</strong>
            <span className="muted small">{t(`mb.how_${k}_body`)}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

// ---- the shared inbox ---------------------------------------------------------------------------

function InboxTab({ o, onChange }: { o: Overview; onChange: () => Promise<unknown> }) {
  const [params, setParams] = useSearchParams();
  const filter = (FILTERS as readonly string[]).includes(params.get('f') ?? '') ? (params.get('f') as Filter) : 'new';
  const box = params.get('box');
  const open = params.get('m');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v === null) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: k !== 'm' });
  };
  const input = useMemo(() => ({ state: filter, ...(box ? { box } : {}), ...(query ? { q: query } : {}), automatic: filter === 'ignored' }), [filter, box, query]);
  const { data, reload } = useAction<{ messages: Row[]; more: boolean; counts: Partial<Record<State, number>> }>('mailbox.messages', input);
  const refresh = async () => {
    await reload();
    await onChange();
  };
  return (
    <div className={`mb-inbox${open ? ' reading' : ''}`}>
      <section className="panel mb-list" aria-label={t('mb.tab_inbox')}>
        <div className="mb-tools">
          <div className="segmented subtle" role="radiogroup" aria-label={t('mb.show')}>
            {FILTERS.map((f) => (
              <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => set('f', f === 'new' ? null : f)}>
                {t(`mb.f_${f}`)}
                {f === 'new' && data?.counts.new ? <span className="dev-tab-count">{data.counts.new}</span> : null}
              </button>
            ))}
          </div>
          <div className="mb-tools-row">
            <label className="search">
              <Icon name="search" size={16} />
              <input className="input" type="search" value={q} placeholder={t('mb.search')} aria-label={t('mb.search')} onChange={(e) => setQ(e.target.value)} />
            </label>
            {o.boxes.length > 1 ? (
              <select className="input" value={box ?? ''} aria-label={t('mb.mailbox')} onChange={(e) => set('box', e.target.value || null)}>
                <option value="">{t('mb.all_boxes')}</option>
                {o.boxes.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        </div>
        {!data ? (
          <div className="mb-rows">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} h={64} />
            ))}
          </div>
        ) : !data.messages.length ? (
          <EmptyState
            icon={filter === 'new' ? 'checkCircle' : 'inbox'}
            title={filter === 'new' && !query ? t('mb.all_handled') : t('mb.nothing')}
            body={filter === 'new' && !query ? t('mb.all_handled_body') : undefined}
          />
        ) : (
          <ul className="mb-rows">
            {data.messages.map((m) => {
              const st = msgState(m);
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    className={`mb-row${open === m.id ? ' active' : ''}${m.state === 'new' ? ' unread' : ''}`}
                    onClick={() => set('m', m.id)}
                    aria-current={open === m.id ? 'true' : undefined}
                  >
                    <span className="mb-row-top">
                      <strong className="mb-from">{who(m)}</strong>
                      <time className="muted small" dateTime={m.received_at} title={dateTime(m.received_at)}>
                        {since(m.received_at)}
                      </time>
                    </span>
                    <span className="mb-subject">{m.subject || t('mb.no_subject')}</span>
                    <span className="mb-preview muted small">{m.preview || '—'}</span>
                    <span className="mb-row-tags">
                      <StatusBadge tone={st.tone} icon={st.icon} label={st.label} />
                      {o.boxes.length > 1 ? <span className="dev-chip">{m.box_name}</span> : null}
                      {m.files ? (
                        <span className="mb-clip muted small" aria-label={tn('mb.files_n', m.files)}>
                          <Icon name="file" size={14} /> {m.files}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {data?.more ? <p className="muted small mb-more">{t('mb.more')}</p> : null}
      </section>
      <section className="panel mb-read" aria-live="polite">
        {open ? <Reader id={open} onBack={() => set('m', null)} onChange={refresh} /> : <EmptyState icon="mail" title={t('mb.pick')} body={t('mb.pick_body')} />}
      </section>
    </div>
  );
}

function Reader({ id, onBack, onChange }: { id: string; onBack: () => void; onChange: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const { data: m, reload } = useAction<Omit<Full, 'files'> & FullFiles>('mailbox.message', { id });
  const [whole, setWhole] = useState(false);
  useEffect(() => setWhole(false), [id]);
  if (!m) return <Skeleton h={300} />;
  const st = msgState(m as unknown as Row);
  const handle = (to: 'helpdesk' | 'crm' | 'ignore' | 'reopen', done: string) =>
    void run(async () => {
      await action('mailbox.handle', { id: m.id, to });
      await reload();
      await onChange();
    }, done);
  const text = whole ? (m.body ?? '') : m.new_part;
  const longer = (m.body ?? '').trim().length > m.new_part.trim().length + 5;
  return (
    <article className="mb-mail">
      <button type="button" className="link-button dev-back mb-back" onClick={onBack}>
        <Icon name="back" size={16} /> {t('mb.back')}
      </button>
      <header className="mb-mail-head">
        <h2>{m.subject || t('mb.no_subject')}</h2>
        <StatusBadge tone={st.tone} icon={st.icon} label={st.label} />
      </header>
      <dl className="mb-meta">
        <div>
          <dt>{t('mb.from')}</dt>
          <dd>
            {m.from_name ? <strong>{m.from_name} </strong> : null}
            {m.from_email ? <span className="muted">&lt;{m.from_email}&gt;</span> : t('mb.unknown_sender')}
          </dd>
        </div>
        <div>
          <dt>{t('mb.to')}</dt>
          <dd>{[...m.to_list, ...m.cc_list].join(', ') || m.box_email}</dd>
        </div>
        <div>
          <dt>{t('mb.received')}</dt>
          <dd>
            {dateTime(m.received_at)} · {m.box_name}
          </dd>
        </div>
      </dl>
      {m.state === 'done' && m.result_id ? (
        <p className="callout callout-info">
          <Icon name={m.result_kind === 'ticket' ? 'lifebuoy' : m.result_kind === 'outreach' ? 'reply' : 'trendUp'} size={18} />
          <span>
            {m.result_kind === 'ticket'
              ? t('mb.became_ticket', { n: m.result_label ?? '' })
              : m.result_kind === 'outreach'
                ? t('mb.became_outreach', { company: m.result_label ?? '' })
                : t('mb.became_lead', { title: m.result_label ?? '' })}
            {noteText(m.note) ? ` ${noteText(m.note)}` : ''}
            {m.handled_by_name ? ` ${t('mb.by', { name: m.handled_by_name })}` : ''}
          </span>
          <Link className="btn btn-sm" to={resultLink(m as unknown as Row)}>
            {m.result_kind === 'ticket' ? t('mb.open_ticket') : m.result_kind === 'outreach' ? t('mb.open_outreach') : t('mb.open_lead')}
          </Link>
        </p>
      ) : m.note ? (
        <p className={`callout ${m.state === 'failed' ? 'callout-danger' : 'callout-warn'}`}>
          <Icon name={m.state === 'failed' ? 'alert' : 'info'} size={18} />
          <span>{noteText(m.note)}</span>
        </p>
      ) : null}
      {m.state !== 'done' ? (
        <div className="mb-acts">
          {m.can.helpdesk ? (
            <Button variant={m.state === 'new' ? 'primary' : 'secondary'} icon="lifebuoy" busy={busy} disabled={!m.from_email} onClick={() => handle('helpdesk', t('mb.made_ticket'))}>
              {t('mb.make_ticket')}
            </Button>
          ) : null}
          {m.can.crm ? (
            <Button icon="trendUp" busy={busy} disabled={!m.from_email} onClick={() => handle('crm', t('mb.made_lead'))}>
              {t('mb.make_lead')}
            </Button>
          ) : null}
          {m.state === 'new' ? (
            <Button variant="ghost" icon="archive" busy={busy} onClick={() => handle('ignore', t('mb.set_aside_done'))}>
              {t('mb.set_aside')}
            </Button>
          ) : (
            <Button variant="ghost" icon="undo" busy={busy} onClick={() => handle('reopen', t('mb.reopened'))}>
              {t('mb.reopen')}
            </Button>
          )}
        </div>
      ) : null}
      <div className="mb-body">{text.trim() ? text : <span className="muted">{t('mb.no_text')}</span>}</div>
      {longer ? (
        <button type="button" className="link-button mb-whole" onClick={() => setWhole(!whole)} aria-expanded={whole}>
          <Icon name={whole ? 'up' : 'down'} size={14} /> {whole ? t('mb.hide_quoted') : t('mb.show_quoted')}
        </button>
      ) : null}
      {m.files.length ? (
        <div className="mb-files">
          <h3 className="dev-h4">{tn('mb.files_n', m.files.length)}</h3>
          <ul>
            {m.files.map((f) => (
              <li key={f.id}>
                <Icon name="file" size={16} />
                <span className="mb-file-name">{f.name}</span>
                <span className="muted small">{sizeText(f.size)}</span>
                {f.kept ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="download"
                    onClick={() =>
                      void run(async () => {
                        const r = await action<{ name: string; mime: string; data: string }>('mailbox.download', { id: f.id });
                        const blob = new Blob([Uint8Array.from(atob(r.data), (c) => c.charCodeAt(0))], { type: r.mime });
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = r.name;
                        a.click();
                        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
                      })
                    }
                  >
                    {t('mb.download')}
                  </Button>
                ) : (
                  <span className="muted small">{t('mb.file_too_large')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  );
}

// ---- the mailboxes ------------------------------------------------------------------------------

function BoxesTab({ o, onEdit, onChange }: { o: Overview; onEdit: (b: BoxRow) => void; onChange: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [drop, setDrop] = useState<BoxRow | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  return (
    <div className="mb-boxes">
      {o.boxes.map((b) => {
        const st = boxState(b);
        return (
          <section key={b.id} className="panel mb-box" aria-label={b.name}>
            <div className="dev-hook-head">
              <div className="dev-hook-title">
                <h2 className="dev-h2">
                  <Icon name={ROUTE_ICON[b.route]} size={18} /> {b.name}
                </h2>
                <StatusBadge tone={st.tone} icon={st.icon} label={st.label} />
              </div>
              <div className="dev-hook-acts">
                <Button
                  size="sm"
                  icon="repeat"
                  busy={busy && checking === b.id}
                  disabled={!b.active}
                  onClick={() =>
                    void run(async () => {
                      setChecking(b.id);
                      try {
                        const r = await request<{ read: number; error: string | null }>('POST', '/api/mailbox/check', { id: b.id });
                        await onChange();
                        if (r.error) throw new Error(serverText(r.error));
                      } finally {
                        setChecking(null);
                      }
                    }, t('mb.checked'))
                  }
                >
                  {t('mb.check_now')}
                </Button>
                <IconButton icon="edit" label={t('mb.edit_n', { name: b.name })} onClick={() => onEdit(b)} />
                <IconButton icon="trash" label={t('mb.remove_n', { name: b.name })} onClick={() => setDrop(b)} />
              </div>
            </div>
            <div className="dev-url">
              <Icon name="at" size={14} />
              <code>{b.email}</code>
              <span className="muted small">
                · {b.host}:{b.port} · {b.folder}
              </span>
            </div>
            {b.error ? (
              <p className="callout callout-danger" role="status">
                <Icon name="alert" size={18} />
                <span>
                  <strong>{t('mb.cannot_read')}</strong> {serverText(b.error)}
                  {b.password_readable === false ? ` ${t('mb.retype_password')}` : ''}
                </span>
                <Button size="sm" icon="edit" onClick={() => onEdit(b)}>
                  {t('mb.fix')}
                </Button>
              </p>
            ) : null}
            <dl className="dev-stats">
              <div>
                <dt>{t('mb.goes_to')}</dt>
                <dd>{t(`mb.route_${b.route}`)}</dd>
              </div>
              <div>
                <dt>{t('mb.waiting')}</dt>
                <dd>{b.waiting}</dd>
              </div>
              <div>
                <dt>{t('mb.today')}</dt>
                <dd>{b.today}</dd>
              </div>
              <div>
                <dt>{t('mb.last_read')}</dt>
                <dd>{b.ok_at ? <span title={dateTime(b.ok_at)}>{since(b.ok_at)}</span> : '—'}</dd>
              </div>
            </dl>
          </section>
        );
      })}
      {drop ? (
        <Confirm
          title={t('mb.remove_n', { name: drop.name })}
          body={t('mb.remove_body')}
          confirm={t('mb.remove')}
          danger
          busy={busy}
          onClose={() => setDrop(null)}
          onConfirm={() =>
            void run(async () => {
              await action('mailbox.remove_box', { id: drop.id });
              setDrop(null);
              await onChange();
            }, t('mb.removed'))
          }
        />
      ) : null}
    </div>
  );
}

const PORTS: Record<Security, number> = { ssl: 993, starttls: 143 };

function BoxDialog({ o, box, onClose, onSaved }: { o: Overview; box: BoxRow | null; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: box?.name ?? '',
    email: box?.email ?? '',
    route: box?.route ?? ((o.can.helpdesk ? 'helpdesk' : 'keep') as Route),
    host: box?.host ?? '',
    port: box?.port ?? 993,
    security: box?.security ?? ('ssl' as Security),
    username: box?.username ?? '',
    password: '',
    folder: box?.folder ?? 'INBOX',
    active: box?.active ?? true,
  });
  const [tested, setTested] = useState<{ ok: boolean; text: string } | null>(null);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => {
    setF((x) => ({ ...x, [k]: v }));
    setTested(null);
  };
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  const domain = emailOk ? f.email.trim().split('@')[1] : null;
  const ready = f.name.trim().length >= 2 && emailOk && f.host.trim() && f.username.trim() && (box || f.password);
  const body = () => ({
    ...(box ? { id: box.id } : {}),
    name: f.name,
    email: f.email,
    route: f.route,
    host: f.host,
    port: Number(f.port),
    security: f.security,
    username: f.username,
    folder: f.folder || 'INBOX',
    active: f.active,
    ...(f.password ? { password: f.password } : {}),
  });
  const routes: Route[] = ['helpdesk', 'crm', 'keep'];
  const allowed = (r: Route) => r === 'keep' || o.can[r];
  return (
    <Dialog
      title={box ? t('mb.edit_n', { name: box.name }) : t('mb.connect')}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button
            variant="ghost"
            icon="repeat"
            busy={busy && tested === null}
            disabled={!f.host.trim() || !f.username.trim() || (!box && !f.password)}
            onClick={() =>
              void run(async () => {
                const r = await action<{ ok: boolean; messages: number | null; error: string | null }>('mailbox.test_box', body());
                setTested({ ok: r.ok, text: r.ok ? tn('mb.test_ok', r.messages ?? 0) : serverText(r.error ?? '') });
              })
            }
          >
            {t('mb.test')}
          </Button>
          <span className="mb-foot-gap" />
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!ready}
            onClick={() =>
              void run(
                async () => {
                  await action('mailbox.save_box', body());
                  await onSaved();
                  onClose();
                },
                box ? t('mb.saved') : t('mb.connected'),
              )
            }
          >
            {box ? t('common.save') : t('mb.connect_it')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="dev-two">
          <Field label={t('mb.name')} hint={t('mb.name_hint')} required>
            <input className="input" value={f.name} maxLength={60} placeholder={t('mb.name_ph')} onChange={(e) => set('name', e.target.value)} autoFocus={!box} />
          </Field>
          <Field label={t('mb.address')} hint={t('mb.address_hint')} error={f.email && !emailOk ? t('mb.address_bad') : null} required>
            <input
              className="input"
              type="email"
              inputMode="email"
              autoComplete="off"
              value={f.email}
              maxLength={200}
              placeholder="support@example.com"
              onChange={(e) => set('email', e.target.value.trim())}
              onBlur={() => {
                if (!f.username && emailOk) set('username', f.email.trim().toLowerCase());
              }}
            />
          </Field>
        </div>
        <fieldset className="dev-fieldset">
          <legend className="field-label">{t('mb.goes_to')}</legend>
          <div className="mb-choice">
            {routes.map((r) => (
              <label key={r} className={`dev-choice-card${f.route === r ? ' on' : ''}${allowed(r) ? '' : ' mb-off'}`}>
                <input type="radio" name="mb-route" checked={f.route === r} disabled={!allowed(r)} onChange={() => set('route', r)} />
                <Icon name={ROUTE_ICON[r]} size={18} />
                <span>
                  <strong>{t(`mb.route_${r}`)}</strong>
                  <span className="muted small">{allowed(r) ? t(`mb.route_${r}_hint`) : t('mb.route_unavailable')}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="dev-fieldset">
          <legend className="field-label">{t('mb.server')}</legend>
          <p className="muted small">{t('mb.server_hint')}</p>
          <div className="mb-server">
            <Field label={t('mb.host')} required>
              <input
                className="input mono"
                value={f.host}
                maxLength={200}
                autoComplete="off"
                spellCheck={false}
                placeholder={domain ? `imap.${domain}` : 'imap.example.com'}
                onChange={(e) => set('host', e.target.value.trim().toLowerCase())}
              />
            </Field>
            <Field label={t('mb.security')}>
              <select
                className="input"
                value={f.security}
                onChange={(e) => {
                  const s = e.target.value as Security;
                  setF((x) => ({ ...x, security: s, port: x.port === PORTS[x.security] ? PORTS[s] : x.port }));
                  setTested(null);
                }}
              >
                <option value="ssl">{t('mb.sec_ssl')}</option>
                <option value="starttls">{t('mb.sec_starttls')}</option>
              </select>
            </Field>
            <Field label={t('mb.port')}>
              <input className="input mono" type="number" inputMode="numeric" min={1} max={65535} value={f.port} onChange={(e) => set('port', Number(e.target.value))} />
            </Field>
          </div>
          <div className="dev-two">
            <Field label={t('mb.username')} hint={t('mb.username_hint')} required>
              <input className="input" value={f.username} maxLength={200} autoComplete="off" spellCheck={false} onChange={(e) => set('username', e.target.value.trim())} />
            </Field>
            <Field label={t('mb.password')} hint={box ? t('mb.password_keep') : t('mb.password_hint')} required={!box}>
              <input className="input" type="password" value={f.password} maxLength={500} autoComplete="new-password" onChange={(e) => set('password', e.target.value)} />
            </Field>
          </div>
          <Field label={t('mb.folder')} hint={t('mb.folder_hint')}>
            <input className="input mono" value={f.folder} maxLength={200} spellCheck={false} onChange={(e) => set('folder', e.target.value)} />
          </Field>
        </fieldset>
        {tested ? (
          <p className={`callout ${tested.ok ? 'callout-info' : 'callout-danger'}`} role="status">
            <Icon name={tested.ok ? 'checkCircle' : 'alert'} size={18} />
            <span>
              <strong>{tested.ok ? t('mb.test_ok_title') : t('mb.test_failed')}</strong> {tested.text}
            </span>
          </p>
        ) : null}
        {box ? (
          <div className="setting-row">
            <div>
              <strong>{t('mb.active')}</strong>
              <p className="muted">{t('mb.active_hint')}</p>
            </div>
            <Switch checked={f.active} onChange={(v) => set('active', v)} label={t('mb.active')} />
          </div>
        ) : null}
        <p className="callout callout-info">
          <Icon name="shieldCheck" size={18} />
          <span>{t('mb.only_reads')}</span>
        </p>
      </div>
    </Dialog>
  );
}
